import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, Lock, User, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

// Bileşenler
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from './ui/card';

// Form doğrulama şeması (Zod) - email yerine username
const loginSchema = z.object({
  username: z
    .string()
    .min(1, { message: 'login.errors.required_username' }),
  password: z
    .string()
    .min(1, { message: 'login.errors.required_password' })
    .min(6, { message: 'login.errors.min_password' })
});

/**
 * Giriş formu bileşeni
 * @returns {JSX.Element} - Giriş formu
 */
const LoginForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login, loading: authLoading, error: authError } = useAuth();

  // State tanımlamaları
  const [usernameValue, setUsernameValue] = useState('');
  const [passwordValue, setPasswordValue] = useState('');
  const [usernameValid, setUsernameValid] = useState(true);
  const [passwordValid, setPasswordValid] = useState(true);
  const [loginError, setLoginError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loginSuccess, setLoginSuccess] = useState(false);

  // Login butonuna tıklandığında
  const handleLogin = async (e) => {
    e.preventDefault();
    
    // Form validasyonu
    let formIsValid = true;
    
    if (!usernameValue || usernameValue.trim() === '') {
      setUsernameValid(false);
      formIsValid = false;
    } else {
      setUsernameValid(true);
    }
    
    if (!passwordValue || passwordValue.trim() === '' || passwordValue.length < 6) {
      setPasswordValid(false);
      formIsValid = false;
    } else {
      setPasswordValid(true);
    }
    
    if (!formIsValid) {
      return;
    }
    
    // Auth context ile giriş yap
    loginWithAuth();
  };

  // Auth context ile giriş yapma
  const loginWithAuth = async () => {
    setIsLoading(true);
    setLoginError('');
    setLoginSuccess(false);
    
    try {
      // Auth context'ten login fonksiyonunu kullan
      const { success, error } = await login(usernameValue, passwordValue);
      
      if (success) {
        setLoginSuccess(true);
        
        // Hesaplama sayfasına yönlendir
        navigate('/calculator');
      } else {
        setLoginError(error || t('login.errors.login_failed'));
      }
    } catch (error) {
      setLoginError(t('login.errors.login_failed'));
    } finally {
      setIsLoading(false);
    }
  };

  // Form temizleme
  const handleClearAll = (e) => {
    e.preventDefault();
    setUsernameValue('');
    setPasswordValue('');
    setUsernameValid(true);
    setPasswordValid(true);
    setLoginError('');
  };
  
  // Oturum kontrolü yaparak sayfa yüklendiğinde oturum varsa yönlendir
  useEffect(() => {
    const session = localStorage.getItem('alasfireSession');
    if (session) {
      navigate('/calculator');
    }
  }, [navigate]);

  return (
    <div className="text-white">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold tracking-tight">{t('login.form_title')}</h2>
        <p className="text-white/70 mt-2 text-sm">{t('login.form_description')}</p>
      </div>
      
      {/* Hata mesajı */}
      {(loginError || authError) && (
        <div className="bg-red-900/30 border border-red-500/30 rounded-lg p-3 mb-5 flex items-center gap-2 text-white">
          <AlertCircle className="h-4 w-4 text-red-400" />
          <p className="text-sm">{loginError || authError}</p>
        </div>
      )}
      
      {/* Başarı mesajı */}
      {loginSuccess && (
        <div className="bg-green-900/30 border border-green-500/30 rounded-lg p-3 mb-5 text-white">
          <p className="text-sm">{t('login.success_message')}</p>
        </div>
      )}
      
      <form onSubmit={handleLogin} className="space-y-5">
        {/* Kullanıcı adı */}
        <div>
          <label htmlFor="username" className="block text-sm font-medium text-white/90 mb-1.5">
            {t('login.username')}
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <User className="h-4 w-4 text-white/40" />
            </div>
            <input
              id="username"
              type="text"
              value={usernameValue}
              onChange={(e) => setUsernameValue(e.target.value)}
              placeholder={t('login.username_placeholder')}
              className={`pl-10 w-full py-2.5 bg-white/5 border ${!usernameValid ? 'border-red-500/50' : 'border-white/10'} rounded-lg focus:ring-2 focus:ring-red-500/30 focus:border-red-500/30 transition-all backdrop-blur-sm text-white placeholder-white/30 text-sm`}
            />
          </div>
          {!usernameValid && (
            <p className="mt-1 text-xs text-red-300">{t('login.errors.required_username')}</p>
          )}
        </div>
        
        {/* Şifre */}
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-white/90 mb-1.5">
            {t('login.password')}
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Lock className="h-4 w-4 text-white/40" />
            </div>
            <input
              id="password"
              type="password"
              value={passwordValue}
              onChange={(e) => setPasswordValue(e.target.value)}
              placeholder={t('login.password_placeholder')}
              className={`pl-10 w-full py-2.5 bg-white/5 border ${!passwordValid ? 'border-red-500/50' : 'border-white/10'} rounded-lg focus:ring-2 focus:ring-red-500/30 focus:border-red-500/30 transition-all backdrop-blur-sm text-white placeholder-white/30 text-sm`}
            />
          </div>
          {!passwordValid && (
            <p className="mt-1 text-xs text-red-300">
              {passwordValue === '' ? t('login.errors.required_password') : t('login.errors.min_password')}
            </p>
          )}
        </div>
        
        <div className="flex justify-between items-center pt-2">
          <button
            type="button"
            onClick={handleClearAll}
            disabled={isLoading || authLoading}
            className="text-xs text-white/50 hover:text-white transition-colors"
          >
            {t('common.clear')}
          </button>
          
          <button
            type="submit"
            disabled={isLoading || authLoading}
            className="inline-flex items-center justify-center px-5 py-2 border border-red-500/20 rounded-lg shadow-sm text-sm font-medium text-white bg-gradient-to-r from-red-700/40 to-red-600/40 hover:from-red-600/50 hover:to-red-500/50 focus:outline-none focus:ring-2 focus:ring-red-500/30 focus:ring-offset-2 focus:ring-offset-red-900 transition-colors backdrop-blur-sm"
          >
            {(isLoading || authLoading) ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('login.logging_in')}
              </>
            ) : (
              t('login.login_button')
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default LoginForm; 